import React from 'react';
import Icon from '../../shared/Icon';
import Chip from '../../shared/Chip';
import './AlertSteps.css';
import { CHANNELS, SEVERITIES } from '../alertSpec';

/**
 * Channel and severity selection, plus the rendered message preview per channel.
 *
 * **SMS is disabled with the reason that it cannot be delivered (AC10).** The
 * API refuses `sms` as a capability gap rather than an unknown value — it has
 * no transport, no phone number on the user record and no vendor decision — so
 * an alert configured for it would evaluate on schedule, pass every gate and
 * notify nobody. Disabling it here with that reason means the author reads it
 * at the point of choice instead of hitting the refusal on create.
 *
 * This is not a severity floor. No such floor exists; that rule and its
 * decision live on PRD-1257, which owns channel resolution.
 *
 * `channelPreviews` answers "no message renderer is registered for this
 * channel yet" for a channel that cannot render, rather than inventing a
 * preview, so an unavailable entry is shown as its reason and not as an
 * empty message.
 */
const AlertChannelPicker = ({ formData, onSetFormData, previews = [] }) => {
  const alert = formData.alert ?? {};
  const selected = alert.channels ?? [];
  const severity = alert.severity ?? 'warning';

  const setAlertField = (patch) => {
    onSetFormData((prev) => ({ ...prev, alert: { ...(prev.alert ?? {}), ...patch } }));
  };

  const toggleChannel = (id, deliverable) => {
    // Guarded here as well as by the disabled attribute — an undeliverable
    // channel must not reach the payload, because the API refuses a mixed
    // list whole rather than narrowing it to the part that works.
    if (!deliverable) return;
    const next = selected.includes(id)
      ? selected.filter((c) => c !== id)
      : [...selected, id];
    setAlertField({ channels: next });
  };

  return (
    <div className="alert-channel-block">
      <h4 className="alert-threshold-heading">Channels and severity</h4>

      <div className="resui-field">
        <span className="resui-field-label">
          Channels <span className="resui-field-required">*</span>
        </span>
        <div className="alert-channel-list">
          {CHANNELS.map((channel) => (
            <label
              key={channel.id}
              className={`resui-checkbox alert-channel-option${channel.deliverable ? '' : ' alert-channel-disabled'}`}
            >
              <input
                type="checkbox"
                className="resui-checkbox-input"
                checked={selected.includes(channel.id)}
                disabled={!channel.deliverable}
                onChange={() => toggleChannel(channel.id, channel.deliverable)}
              />
              <span className="resui-checkbox-mark" />
              <span className="alert-channel-name">{channel.label}</span>
              {!channel.deliverable && (
                <span className="alert-channel-reason">{channel.reason}</span>
              )}
            </label>
          ))}
        </div>
        {selected.length === 0 && (
          <div className="resui-banner resui-banner--alert alert-inline-block">
            <Icon name="warning_amber" />
            <span className="resui-banner-text">
              A channel is required. An alert with no channel evaluates and then
              has nowhere to go.
            </span>
          </div>
        )}
      </div>

      <div className="resui-field">
        <label className="resui-field-label" htmlFor="alert-severity">
          Severity <span className="resui-field-required">*</span>
        </label>
        <select
          id="alert-severity"
          className="resui-select"
          value={severity}
          onChange={(e) => setAlertField({ severity: e.target.value })}
        >
          {SEVERITIES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {previews.length > 0 && (
        <div className="alert-preview-list">
          <h4 className="alert-threshold-heading">Message preview</h4>
          {previews.map((preview) => (
            <div key={preview.channel} className="resui-card alert-preview-card">
              <div className="alert-preview-head">
                <span className="alert-preview-channel">{preview.channel}</span>
                {!preview.available && (
                  <Chip className="alert-preview-unavailable">Unavailable</Chip>
                )}
              </div>
              {preview.available ? (
                <>
                  {preview.subject && (
                    <p className="alert-preview-subject">{preview.subject}</p>
                  )}
                  {preview.body && <p className="alert-preview-body">{preview.body}</p>}
                </>
              ) : (
                <p className="alert-preview-body">{preview.reason}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertChannelPicker;
